import React from "react";
import styled from "styled-components";
import { device } from "../../../utils";

// ELEMENT CYCLE CHART : a plain regular polygon (triangle or square/diamond,
// driven purely by how many elements are passed in) with a small filled
// triangle on each edge pointing from one element to the one it defeats,
// looping all the way around back to the start.
//
// `elements` must be listed in defeat order — elements[i] defeats
// elements[i + 1], wrapping at the end back to elements[0].

const ARROW_BASE = 5;                          // arrowhead base width
const ARROW_HEIGHT = ARROW_BASE * (Math.sqrt(3) / 2); // equilateral height
const RADIUS = 34;                             // polygon vertex radius
const LABEL_RADIUS = 47;                       // label distance, past the vertex

// Regular n-gon, first vertex pointing straight up (so a square lands with
// points at N/E/S/W, and a triangle lands with one point at the top).
function polygonPoints(n, radius) {
  return Array.from({ length: n }, (_, i) => {
    const angle = (-90 + i * (360 / n)) * (Math.PI / 180);
    return {
      x: 50 + radius * Math.cos(angle),
      y: 50 + radius * Math.sin(angle),
    };
  });
}

// Small filled triangle centered on the edge from `from` to `to`, pointing
// toward `to` (the element being defeated).
function arrowheadPoints(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const tipX = mx + ux * (ARROW_HEIGHT / 2);
  const tipY = my + uy * (ARROW_HEIGHT / 2);
  const baseX = mx - ux * (ARROW_HEIGHT / 2);
  const baseY = my - uy * (ARROW_HEIGHT / 2);
  return [
    `${tipX},${tipY}`,
    `${baseX + px * (ARROW_BASE / 2)},${baseY + py * (ARROW_BASE / 2)}`,
    `${baseX - px * (ARROW_BASE / 2)},${baseY - py * (ARROW_BASE / 2)}`,
  ].join(' ');
}

const ChartWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 0 15px;
  display: inline-flex;

  @media ${device.sm} {
    margin: 0 40px;
  }

  @media ${device.xl} {
    margin: 0 50px;
  }

  svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
    // EntryWrap (src/sections/_lux/home/EntryEye.js) has a bare
    // transform rule (-50%, -50%) rule that cascades down
    // to every svg on the page, this chart's included -- reset it here.
    transform: none !important;
  }

  .chart-label {
    position: absolute;
    font-size: 14px;
    top:50%;
    left:50%;
    transform: translate(-50%,0);
    color: #000;
    padding: 3px 10px;
    width: 180px;

    &.quad-chart {
      width: 205px;
      transform: translate(-50%, -14px);
    }
  }

  .cycle-label {
    position: absolute;
    padding: 3px 8px 3px 11px; // Keep this the text aligns weird otherwise
    border: 1px solid;
    font-family: 'pixel9';
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
    white-space: nowrap;
    transform: translateX(-50%);

    @media (max-width: 767px) {
      font-size: 11px;
      padding: 2px 6px 2px 10px;
    }

    // TRIANGLE LABEL ADJUSTMENTS
    &.Void,
    &.Stellar {
      top: 64% !important;
    }
    &.Void { left: 92% !important; }
    &.Stellar { left: 4% !important; }

    // SQUARE LABEL ADJUSTMENTS
    &.Ice,
    &.Earth {
      top: 46% !important;
    }
    &.Ice { left: 96% !important; }
    &.Earth { left: 1% !important; }
    &.Air { top: 89% !important; }
  }
`;

const ElementCycleChart = ({ elements, color }) => {
  const n = elements.length;
  const vertices = polygonPoints(n, RADIUS);
  const labelPositions = polygonPoints(n, LABEL_RADIUS);
  const outline = vertices.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <ChartWrap style={{ color }}>

      {/* CHART TITLE */}
      <div className={`chart-label ${elements.length === 3 ? 'tri-chart':'quad-chart'}`}
        style={{background: `${color}`}}>
        {`${elements.length === 3 ? 'Meta Elements':'Physical Elements'}`}
      </div>

      {/* CHART BODY */}
      <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        <polygon points={outline} fill="none" stroke={color} strokeWidth="0.5" />
        {elements.map((name, i) => (
          <polygon
            key={name}
            points={arrowheadPoints(vertices[i], vertices[(i + 1) % n])}
            fill={color}
          />
        ))}
      </svg>
      {elements.map((name, i) => (
        <div
          key={name}
          className={`cycle-label ${name}`}
          style={{ left: `${labelPositions[i].x}%`, top: `${labelPositions[i].y}%` }}
        >
          {name}
        </div>
      ))}
    </ChartWrap>
  );
};

export default ElementCycleChart;
